import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Paperclip, Download, FileText, Search } from "lucide-react";
import { Toaster } from "sonner";

const fileIconFor = (mime, name) => {
    const n = (name || "").toLowerCase();
    if ((mime || "").includes("pdf") || n.endsWith(".pdf")) return "PDF";
    if ((mime || "").includes("sheet") || n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv")) return "XLS";
    if ((mime || "").includes("word") || n.endsWith(".docx") || n.endsWith(".doc")) return "DOC";
    if ((mime || "").startsWith("image/")) return "IMG";
    return "FILE";
};

export default function DistributorPriceList() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/distributor-price-list")
            .then((r) => setData(r.data))
            .catch(() => setData({ columns: [], rows: [], attachments: [] }));
    }, []);

    if (!data) return <div className="text-sm text-stone-500">Loading…</div>;

    const needle = q.trim().toLowerCase();
    const rows = !needle ? data.rows : data.rows.filter((r) =>
        (r.name || "").toLowerCase().includes(needle)
        || (r.size_label || "").toLowerCase().includes(needle)
        || (r.sku || "").toLowerCase().includes(needle)
        || (r.notes || "").toLowerCase().includes(needle),
    );

    return (
        <div data-testid="distributor-price-list">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700">Wholesale</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Price list</h1>
                    <p className="text-sm text-stone-600 mt-2">
                        Your latest distributor pricing — kept in sync with the order portal. Any attached files (PDF, Excel, Word) are downloadable below.
                    </p>
                    {data.updated_at && (
                        <p className="text-[11px] text-stone-500 mt-2">Last updated {new Date(data.updated_at).toLocaleString()}</p>
                    )}
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search medicine, size, SKU…" className="rounded-none border-stone-300 pl-9 w-72" data-testid="pl-search" />
                </div>
            </div>

            {/* GRID */}
            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-stone-500">Medicine</th>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-stone-500">Size</th>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-stone-500">SKU</th>
                            {data.columns.map((c) => (
                                <th key={c.id} className="text-right p-3 text-[10px] uppercase tracking-widest text-stone-500">{c.name}</th>
                            ))}
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-stone-500">Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={4 + data.columns.length} className="p-10 text-center text-stone-500">
                                {data.rows.length === 0 ? "No prices published yet — please check back soon." : "No matches."}
                            </td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} className="border-b border-stone-100 hover:bg-sky-50/30">
                                <td className="p-3 font-medium text-slate-900">{r.name || "—"}</td>
                                <td className="p-3 font-mono text-stone-700">{r.size_label || "—"}</td>
                                <td className="p-3 font-mono text-xs text-stone-500">{r.sku || "—"}</td>
                                {data.columns.map((c) => (
                                    <td key={c.id} className="p-3 text-right font-mono">
                                        {r.prices?.[c.id] != null && r.prices[c.id] !== "" ? formatINR(r.prices[c.id]) : <span className="text-stone-300">—</span>}
                                    </td>
                                ))}
                                <td className="p-3 text-xs text-stone-600">{r.notes || ""}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ATTACHMENTS */}
            {data.attachments?.length > 0 && (
                <div className="mt-8 bg-white border border-stone-200">
                    <div className="px-4 py-3 bg-sky-50 border-b border-sky-100">
                        <h2 className="font-serif text-xl text-slate-900 flex items-center gap-2"><Paperclip className="w-4 h-4" />Reference files</h2>
                        <p className="text-xs text-stone-600 mt-1">PDFs, Excel sheets, Word docs and images uploaded by HPL admin. Click to view, or download to keep offline.</p>
                    </div>
                    <div className="divide-y divide-stone-100">
                        {data.attachments.map((a) => (
                            <a
                                key={a.id}
                                href={a.url}
                                target="_blank"
                                rel="noreferrer"
                                className="p-3 flex items-center gap-3 text-sm hover:bg-sky-50/40"
                                data-testid={`pl-attachment-${a.id}`}
                            >
                                <span className="text-[10px] font-mono px-2 py-1 bg-stone-100 text-stone-700">{fileIconFor(a.content_type, a.filename)}</span>
                                <span className="text-sky-700 flex-1 truncate" title={a.filename}>{a.filename}</span>
                                <span className="text-xs text-stone-500 whitespace-nowrap">{Math.round((a.size || 0) / 1024)} KB</span>
                                <span className="text-xs text-stone-400 whitespace-nowrap">{new Date(a.uploaded_at).toLocaleDateString()}</span>
                                <Download className="w-4 h-4 text-stone-500" />
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
